/**
 * ScheduleFormDialog — Create/edit dialog for LPA schedules.
 *
 * Features:
 * - Dual-mode: create (no schedule prop) or edit (schedule prop provided)
 * - Name input
 * - Frequency selector (per_shift, daily, weekly, monthly, quarterly)
 * - Auditor picker (reuses OwnerPicker pattern from AuditFormDialog)
 * - Start date (required), end date (optional)
 * - is_active toggle
 * - Info tooltips explaining key concepts
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/core/LanguageContext';
import { useFactory } from '@/core/hooks/useFactory';
import { queryKeys } from '@/core/lib/queryKeys';
import { fetchFactoryUsers } from '@/core/api/user-roles';
import type { LpaScheduleWithDetails, LpaScheduleFrequency } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  Loader2,
  ChevronsUpDown,
  Check,
  Info,
  UserCircle,
  X,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FREQUENCY_OPTIONS: {
  value: LpaScheduleFrequency;
  labelKey: string;
  fallback: string;
}[] = [
  { value: 'per_shift', labelKey: 'mod_lpa.schedules.freq.perShift', fallback: 'Per Shift' },
  { value: 'daily', labelKey: 'mod_lpa.schedules.freq.daily', fallback: 'Daily' },
  { value: 'weekly', labelKey: 'mod_lpa.schedules.freq.weekly', fallback: 'Weekly' },
  { value: 'monthly', labelKey: 'mod_lpa.schedules.freq.monthly', fallback: 'Monthly' },
  { value: 'quarterly', labelKey: 'mod_lpa.schedules.freq.quarterly', fallback: 'Quarterly' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScheduleFormData {
  name: string;
  frequency: LpaScheduleFrequency;
  auditor_id: string | null;
  is_active: boolean;
  start_date: string;
  end_date: string | null;
}

interface ScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, dialog is in "edit" mode. */
  schedule?: LpaScheduleWithDetails | null;
  /** Called with form data when the user submits. Parent controls the mutation. */
  onSubmit: (data: ScheduleFormData) => void;
  isSubmitting?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function emptyForm(): ScheduleFormData {
  return {
    name: '',
    frequency: 'daily',
    auditor_id: null,
    is_active: true,
    start_date: todayISO(),
    end_date: null,
  };
}

function formFromSchedule(schedule: LpaScheduleWithDetails): ScheduleFormData {
  return {
    name: schedule.name,
    frequency: schedule.frequency,
    auditor_id: schedule.auditor_id,
    is_active: schedule.is_active,
    start_date: schedule.start_date,
    end_date: schedule.end_date,
  };
}

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

// ---------------------------------------------------------------------------
// Auditor picker sub-component
// ---------------------------------------------------------------------------

interface AuditorPickerProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
  t: (key: string, fallback: string) => string;
}

function AuditorPicker({ value, onChange, disabled, t }: AuditorPickerProps) {
  const { selectedFactoryId } = useFactory();
  const [open, setOpen] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: queryKeys.factoryUsers(selectedFactoryId),
    queryFn: () => fetchFactoryUsers(selectedFactoryId!),
    enabled: !!selectedFactoryId,
  });

  // Approved users only, deduplicated by user_id
  const approvedUsers = useMemo(() => {
    const seen = new Set<string>();
    return users
      .filter((u) => {
        if (u.status !== 'approved') return false;
        if (seen.has(u.user_id)) return false;
        seen.add(u.user_id);
        return true;
      })
      .map((u) => ({
        id: u.user_id,
        full_name: (u.profile as { full_name?: string | null })?.full_name ?? null,
        email: (u.profile as { email?: string })?.email ?? '',
        role: u.role,
      }));
  }, [users]);

  const selectedUser = approvedUsers.find((u) => u.id === value);

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('mod_lpa.schedules.form.loadingUsers', 'Loading users...')}
              </span>
            ) : selectedUser ? (
              <span className="flex items-center gap-2">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="text-[10px]">
                    {getInitials(selectedUser.full_name, selectedUser.email)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">
                  {selectedUser.full_name ?? selectedUser.email}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t('mod_lpa.schedules.form.selectAuditor', 'Select auditor...')}
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t('mod_lpa.schedules.form.searchAuditor', 'Search by name or email...')}
            />
            <CommandList>
              <CommandEmpty>
                {t('mod_lpa.schedules.form.noUsersFound', 'No users found.')}
              </CommandEmpty>
              <CommandGroup>
                {approvedUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${user.full_name ?? ''} ${user.email}`}
                    onSelect={() => {
                      onChange(user.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === user.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <Avatar className="h-6 w-6 mr-2">
                      <AvatarFallback className="text-[10px]">
                        {getInitials(user.full_name, user.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm truncate">
                        {user.full_name ?? user.email}
                      </span>
                      {user.full_name && (
                        <span className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Clear button */}
      {value && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => onChange(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Info tooltip helper
// ---------------------------------------------------------------------------

function FieldInfo({ children }: { children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] text-xs">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ScheduleFormDialog({
  open,
  onOpenChange,
  schedule,
  onSubmit,
  isSubmitting = false,
}: ScheduleFormDialogProps) {
  const { t } = useLanguage();
  const isEditing = !!schedule;

  const [form, setForm] = useState<ScheduleFormData>(emptyForm);

  // Reset form when dialog opens or schedule changes
  useEffect(() => {
    if (open) {
      setForm(schedule ? formFromSchedule(schedule) : emptyForm());
    }
  }, [open, schedule]);

  const updateField = <K extends keyof ScheduleFormData>(
    field: K,
    value: ScheduleFormData[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isValid = form.name.trim().length > 0 && form.start_date.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;
    onSubmit({
      ...form,
      name: form.name.trim(),
      end_date: form.end_date || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('mod_lpa.schedules.form.editTitle', 'Edit Schedule')
              : t('mod_lpa.schedules.form.createTitle', 'New Schedule')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t(
                  'mod_lpa.schedules.form.editDescription',
                  'Update the schedule layer. Changes only affect future (not yet generated) instances.',
                )
              : t(
                  'mod_lpa.schedules.form.createDescription',
                  'Define a new schedule layer for this audit. Each layer specifies who audits, how often, and when.',
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="schedule-name">
                {t('mod_lpa.schedules.form.nameLabel', 'Schedule Name')} *
              </Label>
              <FieldInfo>
                {t(
                  'mod_lpa.schedules.form.nameHint',
                  'A descriptive name for this layer, e.g., "Layer 1 - Operator daily" or "Supervisor weekly check".',
                )}
              </FieldInfo>
            </div>
            <Input
              id="schedule-name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder={t(
                'mod_lpa.schedules.form.namePlaceholder',
                'e.g., Layer 1 - Operator daily',
              )}
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>
                {t('mod_lpa.schedules.form.frequencyLabel', 'Frequency')} *
              </Label>
              <FieldInfo>
                {t(
                  'mod_lpa.schedules.form.frequencyHint',
                  'How often audit instances are generated. "Per Shift" creates one instance per shift; "Daily" creates one per day, etc.',
                )}
              </FieldInfo>
            </div>
            <Select
              value={form.frequency}
              onValueChange={(val) => updateField('frequency', val as LpaScheduleFrequency)}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey, opt.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Auditor */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>
                <UserCircle className="h-3.5 w-3.5 inline mr-1" />
                {t('mod_lpa.schedules.form.auditorLabel', 'Auditor')}
              </Label>
              <FieldInfo>
                {t(
                  'mod_lpa.schedules.form.auditorHint',
                  'The person assigned to perform this audit layer. Can be left empty — but instances cannot be generated without an auditor.',
                )}
              </FieldInfo>
            </div>
            <AuditorPicker
              value={form.auditor_id}
              onChange={(id) => updateField('auditor_id', id)}
              disabled={isSubmitting}
              t={t}
            />
            {form.auditor_id === null && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {t(
                  'mod_lpa.schedules.form.noAuditorWarning',
                  'No auditor assigned — instances cannot be generated until an auditor is set.',
                )}
              </p>
            )}
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="schedule-start">
                  {t('mod_lpa.schedules.form.startDateLabel', 'Start Date')} *
                </Label>
              </div>
              <Input
                id="schedule-start"
                type="date"
                value={form.start_date}
                onChange={(e) => updateField('start_date', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="schedule-end">
                  {t('mod_lpa.schedules.form.endDateLabel', 'End Date')}
                </Label>
                <FieldInfo>
                  {t(
                    'mod_lpa.schedules.form.endDateHint',
                    'Leave empty for an indefinite schedule. When set, no instances are generated after this date.',
                  )}
                </FieldInfo>
              </div>
              <Input
                id="schedule-end"
                type="date"
                value={form.end_date ?? ''}
                onChange={(e) => updateField('end_date', e.target.value || null)}
                min={form.start_date}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Active toggle — only shown in edit mode (new schedules are always active) */}
          {isEditing && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="schedule-active" className="text-sm font-medium">
                  {t('mod_lpa.schedules.form.activeLabel', 'Active')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'mod_lpa.schedules.form.activeDescription',
                    'Inactive schedules are paused and will not generate new instances.',
                  )}
                </p>
              </div>
              <Switch
                id="schedule-active"
                checked={form.is_active}
                onCheckedChange={(checked) => updateField('is_active', checked)}
                disabled={isSubmitting}
              />
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('mod_lpa.schedules.form.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {isEditing
                ? t('mod_lpa.schedules.form.saveChanges', 'Save Changes')
                : t('mod_lpa.schedules.form.create', 'Create Schedule')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
